import { Link } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState, NoDataIcon } from '@/components/ui/EmptyState';
import { useAlertHistory, useMarkAlertRead, useMarkAllAlertsRead } from '@/hooks/queries/useAlerts';
import { formatAlertType, formatRelativeTime } from '@/lib/formatters';
import { cn } from '@/lib/utils';
import type { AlertSeverity } from '@steward/shared';

export function AlertHistoryList() {
  const { data: alerts, isLoading } = useAlertHistory({ limit: 100 });
  const markRead = useMarkAlertRead();
  const markAllRead = useMarkAllAlertsRead();

  const unreadCount = alerts?.filter((a) => !a.isRead).length || 0;

  const severityVariants: Record<AlertSeverity, 'info' | 'warning' | 'error'> = {
    info: 'info',
    warning: 'warning',
    critical: 'error',
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Alert History</CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-center py-8">
          <Spinner />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div className="flex items-center gap-2">
          <CardTitle>Alert History</CardTitle>
          {unreadCount > 0 && (
            <Badge variant="error">{unreadCount} unread</Badge>
          )}
        </div>
        {unreadCount > 0 && (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => markAllRead.mutate()}
            isLoading={markAllRead.isPending}
          >
            Mark All Read
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {!alerts || alerts.length === 0 ? (
          <EmptyState
            icon={<NoDataIcon />}
            title="No alerts yet"
            description="Alerts will appear here when triggered"
          />
        ) : (
          <div className="space-y-3">
            {alerts.map((alert) => (
              <div
                key={alert.id}
                className={cn(
                  'p-4 rounded-lg border transition-colors cursor-pointer',
                  alert.isRead
                    ? 'border-gray-200 bg-white'
                    : 'border-brand-200 bg-brand-50 hover:bg-brand-100'
                )}
                onClick={() => {
                  if (!alert.isRead) {
                    markRead.mutate(alert.id);
                  }
                }}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge variant={severityVariants[alert.severity]} size="sm">
                        {alert.severity}
                      </Badge>
                      <Badge variant="default" size="sm">
                        {formatAlertType(alert.alertType)}
                      </Badge>
                      {!alert.isRead && (
                        <span className="h-2 w-2 rounded-full bg-brand-600" />
                      )}
                    </div>
                    <h4 className="font-medium text-gray-900">{alert.title}</h4>
                    <p className="text-sm text-gray-600 mt-1">{alert.message}</p>
                    {(alert.tradeChainId || alert.positionId) && (
                      <div className="mt-2">
                        {alert.tradeChainId && (
                          <Link
                            to={`/chains/${alert.tradeChainId}`}
                            className="text-sm text-brand-600 hover:text-brand-700"
                            onClick={(e) => e.stopPropagation()}
                          >
                            View Trade Chain
                          </Link>
                        )}
                      </div>
                    )}
                  </div>
                  <span className="text-xs text-gray-400 whitespace-nowrap">
                    {formatRelativeTime(alert.createdAt)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
